import { atom, useAtomValue, useSetAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { withAtomEffect } from 'jotai-effect';

import {
  clearDarkModeFromStorage,
  DARK_MODE_STORAGE_KEY,
  getSystemPrefersDark,
  readDarkModeFromStorage,
  resolveIsDarkMode,
  writeDarkModeToStorage,
} from './dark-mode-storage';

/** null = follow OS; boolean = explicit user choice. */
type DarkModePreference = boolean | null;

const applyThemeToDOM = (isDark: boolean) => {
  if (isDark) {
    document.documentElement.setAttribute('data-theme', 'dark');
  } else {
    document.documentElement.removeAttribute('data-theme');
  }
};

/** Apply resolved dark-mode before React mounts the header (loading screen, etc.). */
export const syncDarkModeFromStorage = () => {
  if (typeof window === 'undefined') {
    return;
  }
  applyThemeToDOM(resolveIsDarkMode());
};

const preferenceStorage = {
  getItem: (
    _key: string,
    initialValue: DarkModePreference
  ): DarkModePreference => {
    try {
      return readDarkModeFromStorage();
    } catch {
      return initialValue;
    }
  },
  setItem: (_key: string, value: DarkModePreference): void => {
    if (value === null) {
      clearDarkModeFromStorage();
      return;
    }
    writeDarkModeToStorage(value);
  },
  removeItem: (_key: string): void => {
    clearDarkModeFromStorage();
  },
};

const darkModePreferenceAtom = atomWithStorage<DarkModePreference>(
  DARK_MODE_STORAGE_KEY,
  null,
  preferenceStorage,
  { getOnInit: true }
);

const systemPrefersDarkAtom = atom(getSystemPrefersDark());

/**
 * Preference atom that keeps DOM in sync and listens to OS theme while unset.
 */
const darkModePreferenceSyncAtom = withAtomEffect(
  darkModePreferenceAtom,
  (get, set) => {
    const preference = get(darkModePreferenceAtom);

    if (typeof window === 'undefined') {
      return;
    }

    if (preference !== null) {
      applyThemeToDOM(preference);
      return;
    }

    if (typeof window.matchMedia !== 'function') {
      applyThemeToDOM(getSystemPrefersDark());
      return;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const applySystemPreference = () => {
      set(systemPrefersDarkAtom, mediaQuery.matches);
      applyThemeToDOM(mediaQuery.matches);
    };

    applySystemPreference();
    mediaQuery.addEventListener('change', applySystemPreference);
    return () => {
      mediaQuery.removeEventListener('change', applySystemPreference);
    };
  }
);

const isDarkModeAtom = atom(
  (get) => {
    const preference = get(darkModePreferenceSyncAtom);
    return preference === null ? get(systemPrefersDarkAtom) : preference;
  },
  (get, set, update: boolean | ((previous: boolean) => boolean)) => {
    const preference = get(darkModePreferenceAtom);
    const current =
      preference === null ? get(systemPrefersDarkAtom) : preference;
    const next = typeof update === 'function' ? update(current) : update;
    set(darkModePreferenceAtom, next);
  }
);

export const useIsDarkMode = () => useAtomValue(isDarkModeAtom);

export const useToggleDarkMode = () => {
  const setIsDarkMode = useSetAtom(isDarkModeAtom);
  return () => setIsDarkMode((previous) => !previous);
};

// Module init: sync DOM as soon as this package is imported (still after head bootstrap).
syncDarkModeFromStorage();
